import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import {
  Frame,
  FreelancerProfileRecommendations,
  FreelancerProfileContactSection,
  FreelancerExpertiseSection,
  FreelancerRolesSection,
  FreelancerEquipmentSection,
  Box,
  FreelancerProfileCalendarSection,
  FreelancerCreditsSection,
  FreelancerWorkStatusSection,
  FreelancerCommitmentSection,
  FreelancerProfileWLCalendarSection,
  FreelancerDiversitySection,
  FreelancerDemoCalendarSection,
  FreelancerSelfIdentificationSection,
  FreelancerSideRecommendationsSection,
  FreelancerAlumniSection,
  FreelancerDisabilitySection
} from 'components'
import IntroDialog from '../FreelancerMonth/IntroDialog'
import { getUserRecommendations, useHistory } from 'utils'
import FreelancerEditProfile from './FreelancerEditProfile'
import {
  COMMITMENT_STATUS,
  ONTARIO_WORKING_STATUS,
  DIVERSITY_TAGS_ENABLED,
  GENDER_TAGS_ENABLED,
  DISABLE_CRRAL_CALENDAR,
  USE_DEMO_LANDER,
  CREDITS_ENABLED,
  CRRAL_RECOMMENDATIONS_ENABLED,
  SIDE_RECOMMENDATIONS_ENABLED,
} from 'constants'
import { useOnboardConfig } from 'utils';
import { DISABLE_AVAILABILITY } from 'constants'

// Helper function to check if onboardConfig includes an object with a specific name
const hasConfigItem = (config, itemName) => {
  // Debug log to see what the config actually contains
  console.log('onboardConfig in hasConfigItem:', config, 'looking for item:', itemName);
  
  // Handle case when config is an array (old style)
  if (config && Array.isArray(config)) {
    return config.some(item => item.name === itemName && item.enabled !== false);
  }
  // Handle case when config is an object with onboardConfig property (new style)
  else if (config && Array.isArray(config.onboardConfig)) {
    return config.onboardConfig.some(item => item.name === itemName && item.enabled !== false);
  }
  // Default fallback - always enable for development if config is not loaded properly
  return true;
};

const FreelancerProfile = ({ user, setSnackMessage }) => {
  const [recommendations, setRecommendations] = useState([])
  const [editMode, setEditMode] = useState(false)
  const [welcomeOpen, setWelcomeOpen] = useState(false)

  const history = useHistory()
  const params = new URLSearchParams(window.location.search.split('?')[1])
  const isOnboarding = params.get('source') === 'onboarding'

  const onboardConfig = useOnboardConfig()
  
  // Check if user needs to complete signup
  // This is a critical check that ensures users have cities/location data
  useEffect(() => {
    if (user) {
      console.log('FreelancerProfile checking user data:', { 
        cities: user.cities, 
        city: user.city,
        hasRequiredData: !(!user.cities || !user.cities.length || user.cities.length === 0) || !!user.city
      })
      
      // If source is NOT onboarding and required data is missing, redirect to signup
      if (!isOnboarding && (!user.cities || !user.cities.length || user.cities.length === 0) && !user.city) {
        console.log('FreelancerProfile: Missing required city data, redirecting to onboarding')
        history.push('/freelancer-signup')
      }
    }
  }, [user, history, isOnboarding])
  
  // Debug log for onboardConfig
  useEffect(() => {
    console.log('onboardConfig initialized:', onboardConfig);
  }, [onboardConfig])

  useEffect(() => {
    if (isOnboarding) {
      setWelcomeOpen(true)
    }
  }, [])


  useEffect(() => {
    if (!user) return
    getUserRecommendations(user).then((recommendations) => {
      setRecommendations(Object.values(recommendations))
    })
  }, [user])

  if (editMode) {
    return (
      <Frame maxWidth="lg" user={user}>
        <FreelancerEditProfile
          user={user}
          setSnackMessage={setSnackMessage}
          onSubmit={() => setEditMode(false)}
        />
      </Frame>
    )
  }

  return (
    <Frame py={2.5} maxWidth="lg" user={user}>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }}>
        <Box flex={1} mr={2.5}>
          {/* Contact Section - Always visible */}
          <FreelancerProfileContactSection
            onEdit={() => setEditMode(true)}
            freelancer={user}
            history={history}
          />
          
          {/* Diversity Tags */}
          {DIVERSITY_TAGS_ENABLED && hasConfigItem(onboardConfig, 'diversityTags') && (
            <FreelancerDiversitySection
              freelancer={user}
              onEdit={() => {
                if (GENDER_TAGS_ENABLED) {
                  history.push('/gendertags')
                  return
                }
                history.push('/diversitytags')
              }}
            />
          )}

          {/* Self Identification Section */}
          {hasConfigItem(onboardConfig, 'selfIdentification') && (
            <FreelancerSelfIdentificationSection
              freelancer={user}
              onEdit={() => history.push('/ethnicity/edit')}
            />
          )}

          {/* Disability Section */}
          {hasConfigItem(onboardConfig, 'disabilitySelect') && (
            <FreelancerDisabilitySection
              freelancer={user}
              onEdit={() => history.push('/disability/edit')}
            />
          )}

          {/* Roles Section */}
          {hasConfigItem(onboardConfig, 'roles') && (
            <FreelancerRolesSection
              freelancer={user}
              onEdit={() => history.push('/roles/manage')}
            />
          )}

          {/* Expertise Section */}
          {hasConfigItem(onboardConfig, 'primaryRole') && (
            <FreelancerExpertiseSection
              freelancer={user}
              onEdit={() => history.push('/roles')}
            />
          )}

          {/* Credits Section */}
          {CREDITS_ENABLED && hasConfigItem(onboardConfig, 'credits') && 
            <FreelancerCreditsSection 
              freelancer={user} 
              onEdit={() => history.push('/credits')} 
            />
          }

          {/* Equipment Section */}
          {hasConfigItem(onboardConfig, 'equipment') && (
            <FreelancerEquipmentSection
              freelancer={user}
              onEdit={() => history.push('/equipment')}
            />
          )}

          {/* Recommendations */}
          {CRRAL_RECOMMENDATIONS_ENABLED && hasConfigItem(onboardConfig, 'recommendations') && (
            <FreelancerProfileRecommendations
              onEdit={() => history.push('/recommendations/request')}
              freelancer={{ ...user, recommendations }}
            />
          )}
        </Box>
        <Box display='flex' flexDirection='column' maxWidth='30%'>
          {/* Alumni Section */}
          {hasConfigItem(onboardConfig, 'programSelect') && (
            <FreelancerAlumniSection
              freelancer={user}
              onEdit={() => history.push('/programSelect')}
            />
          )}

          {/* Side Recommendations */}
          {SIDE_RECOMMENDATIONS_ENABLED && (
            <FreelancerSideRecommendationsSection
              freelancer={{ ...user, recommendations }}
              onRecommend={() => history.push('/recommendations/request')}
            />
          )}

          {/* Commitment Section */}
          {COMMITMENT_STATUS && hasConfigItem(onboardConfig, 'desiredCommitment') && (
            <FreelancerCommitmentSection
              freelancer={user}
              onEdit={() => history.push('/availability')} />
          )}
          
          {/* Calendar Section */}
          {!DISABLE_CRRAL_CALENDAR && (
            <FreelancerProfileCalendarSection
              freelancer={user}
              onEdit={() => history.push('/month')}
            />
          )}
          
          {/* Demo Calendar */}
          {!DISABLE_AVAILABILITY && hasConfigItem(onboardConfig, 'employmentStatus') && (
            <FreelancerDemoCalendarSection 
              freelancer={user} 
              onEdit={() => history.push('/employmentstatus')} 
            />
          )}
          
          {/* Work Status */}
          {ONTARIO_WORKING_STATUS && hasConfigItem(onboardConfig, 'ontarioWorkingStatus') && (
            <FreelancerWorkStatusSection
              freelancer={user}
              onEdit={() => history.push('/workstatus')}
            />
          )}
          
          {/* WL Calendar */}
          {COMMITMENT_STATUS && hasConfigItem(onboardConfig, 'desiredCommitment') && (
            <FreelancerProfileWLCalendarSection
              freelancer={user}
              onEdit={() => history.push('/availability')}
            />
          )}
          {isOnboarding && <IntroDialog open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />}
        </Box>
      </Box>
    </Frame>
  )
}

export default FreelancerProfile
